"use client";

import { useRef } from "react";
import { PINK_BAND, SectionHead, SectionShell } from "./kit";
import { EASE_OUT, gsap, NOT_REDUCED, playOnEnter, useGSAP } from "./kit/motion";

const DESTS = [
  { id: "ios", label: "App Store", sub: "iOS visitors", x: "17.5%" },
  { id: "android", label: "Google Play", sub: "Android visitors", x: "50%" },
  { id: "web", label: "Web", sub: "Everyone else", x: "82.5%" },
];

/** ViewBox 0 0 800 340. Node bottom ≈ y=78; chip tops ≈ y=280. */
const PATHS = [
  "M400 78 C 400 180, 140 180, 140 276",
  "M400 78 C 400 170, 400 190, 400 276",
  "M400 78 C 400 180, 660 180, 660 276",
];

export default function CanonicalExampleSection() {
  const diagramRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const svg = svgRef.current;
        const diagram = diagramRef.current;
        if (!svg || !diagram) return;
        const paths = gsap.utils.toArray<SVGPathElement>("path", svg);
        const packets = gsap.utils.toArray<SVGCircleElement>("circle", svg);

        // Draw-in on enter: paths trace out, chips rise, packets appear.
        paths.forEach((p) => {
          const len = p.getTotalLength();
          gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
        });
        packets.forEach((c) => c.setAttribute("opacity", "0"));
        const tl = gsap.timeline({ paused: true });
        tl.to(paths, {
          strokeDashoffset: 0,
          duration: 1.1,
          ease: "power2.inOut",
          stagger: 0.15,
        });
        tl.from(
          "[data-canon-chip]",
          { y: 16, opacity: 0, duration: 0.5, ease: EASE_OUT, stagger: 0.12 },
          0.55,
        );
        const disposeEnter = playOnEnter(tl, diagram);

        // Looping packets along the paths; paused whenever off-screen.
        const loops = packets.map((c, i) => {
          const p = paths[i];
          const len = p.getTotalLength();
          const state = { t: 0 };
          return gsap.to(state, {
            t: 1,
            duration: 2.2,
            repeat: -1,
            repeatDelay: 0.9,
            ease: "power1.inOut",
            delay: i * 0.6,
            paused: true,
            onUpdate: () => {
              const pt = p.getPointAtLength(state.t * len);
              c.setAttribute("cx", String(pt.x));
              c.setAttribute("cy", String(pt.y));
              const fade = Math.min(1, Math.min(state.t, 1 - state.t) * 8);
              c.setAttribute("opacity", String(fade));
            },
          });
        });
        const io = new IntersectionObserver((entries) => {
          const visible = entries.some((e) => e.isIntersecting);
          loops.forEach((l) => (visible ? l.play() : l.pause()));
        });
        io.observe(diagram);

        return () => {
          disposeEnter?.();
          io.disconnect();
          loops.forEach((l) => l.kill());
        };
      });
    },
    { scope: diagramRef },
  );

  return (
    <SectionShell id="ref-canonical" band="pink" decoAvoid className="max-md:bg-[#d70e62]">
      <SectionHead
        center
        tone="pink"
        gridFill={PINK_BAND}
        eyebrow="The classic"
        title={<>One link on the poster. Three right answers.</>}
        lead="Print one QR code. iPhones land in the App Store, Androids in Google Play, laptops on your site — the same link, resolved for each visitor as they scan."
      />

      {/* md+ diagram — fixed coordinate space, no measurement needed. */}
      {/* data-grid-block sits on the WIDTH-CONSTRAINED wrapper (#189): on the
          full-width outer div the clearing wiped the pink band's repainted
          hairlines beside the diagram (the over-clearing class of bug). */}
      <div className="hidden md:block">
        <div
          ref={diagramRef}
          data-grid-block
          data-grid-fill={PINK_BAND}
          className="relative mx-auto aspect-[800/340] w-full max-w-[820px]"
        >
          <svg
            ref={svgRef}
            viewBox="0 0 800 340"
            className="absolute inset-0 h-full w-full"
            fill="none"
            aria-hidden
          >
            {PATHS.map((d) => (
              <path key={d} d={d} stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
            ))}
            {PATHS.map((d) => (
              <circle key={`p-${d}`} r="4" fill="#fff" />
            ))}
          </svg>

          {/* The link node. */}
          <div className="absolute left-1/2 top-[17%] -translate-x-1/2 -translate-y-1/2">
            <div className="flex items-center gap-3 rounded-xl bg-white px-5 py-3.5 text-ink shadow-ref-lg">
              <span className="grid h-8 w-8 place-items-center rounded-[8px] bg-brand-600/[0.08] font-jetbrains text-[13px] font-bold text-brand-600">
                ⌘
              </span>
              <span className="font-jetbrains text-[15px] font-semibold">rii.link/launch</span>
            </div>
          </div>

          {/* Destination chips. */}
          {DESTS.map((d) => (
            <div
              key={d.id}
              data-canon-chip
              className="absolute top-[81%] -translate-x-1/2"
              style={{ left: d.x }}
            >
              <div className="rounded-xl border border-white/30 bg-white/[0.12] px-5 py-3 text-center backdrop-blur-[2px]">
                <div className="text-[14.5px] font-bold text-white">{d.label}</div>
                <div className="mt-0.5 font-jetbrains text-[11.5px] text-white/70">{d.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: static stack. */}
      <div className="md:hidden">
        <div className="mx-auto flex w-fit items-center gap-3 rounded-xl bg-white px-5 py-3.5 text-ink shadow-ref-lg">
          <span className="font-jetbrains text-[15px] font-semibold">rii.link/launch</span>
        </div>
        <div className="mt-6 flex flex-col items-center gap-3">
          {DESTS.map((d) => (
            <div key={d.id} className="w-full max-w-[300px] rounded-xl border border-white/30 bg-white/[0.12] px-5 py-3 text-center">
              <div className="text-[14.5px] font-bold text-white">{d.label}</div>
              <div className="mt-0.5 font-jetbrains text-[11.5px] text-white/70">{d.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
